'use client';

/**
 * React Query hooks for workflow feature.
 *
 * Provides query and mutation hooks for pre-review, team review,
 * change requests, deprecation, bulk actions, and duplicate detection.
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from '@tanstack/react-query';
import { translationKeys } from '../translations/queries';
import {
  fetchPreReviewQueue,
  fetchReviewQueue,
  fetchCorrectionsQueue,
  fetchChangeRequestQueue,
  fetchDuplicateAnalysis,
  fetchSimilarByValue,
  submitForReview,
  approvePreReview,
  rejectPreReview,
  approveReview,
  rejectReview,
  requestChange,
  createChangeVersion,
  cancelChangeRequest,
  rejectChangeRequest,
  approveChangeRequest,
  deprecateTranslation,
  rollbackTranslation,
  resubmitCorrection,
  bulkSubmit,
  bulkApprove,
  bulkReject,
  bulkDeprecate,
  bulkAssignTags,
} from './api';
import type {
  WorkflowQueueFilters,
  ApprovePreReviewPayload,
  RejectPreReviewPayload,
  ApproveReviewPayload,
  RejectReviewPayload,
  RequestChangePayload,
  RejectChangeRequestPayload,
  CreateChangeVersionPayload,
  DeprecateTranslationPayload,
  ResubmitCorrectionPayload,
  BulkSubmitPayload,
  BulkApprovePayload,
  BulkRejectPayload,
  BulkDeprecatePayload,
  BulkAssignTagsPayload,
  DuplicateDetectionFilters,
  SearchSimilarByValueFilters,
} from './types';

// ---------- Query Key Factory ----------

export const workflowKeys = {
  all: ['workflow'] as const,
  preReview: () => [...workflowKeys.all, 'pre-review'] as const,
  preReviewQueue: (filters: WorkflowQueueFilters) =>
    [...workflowKeys.preReview(), filters] as const,
  review: () => [...workflowKeys.all, 'review'] as const,
  reviewQueue: (filters: WorkflowQueueFilters) =>
    [...workflowKeys.review(), filters] as const,
  corrections: () => [...workflowKeys.all, 'corrections'] as const,
  correctionsQueue: (filters: WorkflowQueueFilters) =>
    [...workflowKeys.corrections(), filters] as const,
  changeRequests: () => [...workflowKeys.all, 'change-requests'] as const,
  changeRequestQueue: (filters: WorkflowQueueFilters) =>
    [...workflowKeys.changeRequests(), filters] as const,
  duplicates: () => [...workflowKeys.all, 'duplicates'] as const,
  duplicateAnalysis: (filters: DuplicateDetectionFilters) =>
    [...workflowKeys.duplicates(), filters] as const,
  similarByValue: (filters: SearchSimilarByValueFilters) =>
    [...workflowKeys.all, 'similar-by-value', filters] as const,
};

// ---------- Helper: Invalidate all workflow + translation caches ----------

function useWorkflowInvalidation() {
  const queryClient = useQueryClient();

  return () => {
    queryClient.invalidateQueries({ queryKey: workflowKeys.all });
    queryClient.invalidateQueries({ queryKey: translationKeys.all });
  };
}

// ---------- Queue Query Hooks ----------

export function usePreReviewQueue(filters: WorkflowQueueFilters = {}) {
  return useQuery({
    queryKey: workflowKeys.preReviewQueue(filters),
    queryFn: () => fetchPreReviewQueue(filters),
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

export function useReviewQueue(filters: WorkflowQueueFilters = {}) {
  return useQuery({
    queryKey: workflowKeys.reviewQueue(filters),
    queryFn: () => fetchReviewQueue(filters),
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

export function useCorrectionsQueue(filters: WorkflowQueueFilters = {}) {
  return useQuery({
    queryKey: workflowKeys.correctionsQueue(filters),
    queryFn: () => fetchCorrectionsQueue(filters),
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

export function useChangeRequestQueue(filters: WorkflowQueueFilters = {}) {
  return useQuery({
    queryKey: workflowKeys.changeRequestQueue(filters),
    queryFn: () => fetchChangeRequestQueue(filters),
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

// ---------- Duplicate Detection ----------

export function useDuplicateDetection(
  filters: DuplicateDetectionFilters = {},
  enabled = true
) {
  return useQuery({
    queryKey: workflowKeys.duplicateAnalysis(filters),
    queryFn: () => fetchDuplicateAnalysis(filters),
    staleTime: 5 * 60_000, // 5 minutes — analysis is expensive
    enabled,
  });
}

// ---------- Similar By Value ----------

export function useSimilarByValue(
  filters: SearchSimilarByValueFilters,
  enabled = true
) {
  return useQuery({
    queryKey: workflowKeys.similarByValue(filters),
    queryFn: () => fetchSimilarByValue(filters),
    staleTime: 30_000,
    enabled: enabled && filters.value.length >= 1 && !!filters.locale,
  });
}

// ---------- Mutation Hooks ----------

export function useSubmitForReview() {
  const invalidate = useWorkflowInvalidation();

  return useMutation({
    mutationFn: (uuid: string) => submitForReview(uuid),
    onSuccess: invalidate,
  });
}

export function useApprovePreReview() {
  const invalidate = useWorkflowInvalidation();

  return useMutation({
    mutationFn: ({
      uuid,
      payload,
    }: {
      uuid: string;
      payload?: ApprovePreReviewPayload;
    }) => approvePreReview(uuid, payload),
    onSuccess: invalidate,
  });
}

export function useRejectPreReview() {
  const invalidate = useWorkflowInvalidation();

  return useMutation({
    mutationFn: ({
      uuid,
      payload,
    }: {
      uuid: string;
      payload: RejectPreReviewPayload;
    }) => rejectPreReview(uuid, payload),
    onSuccess: invalidate,
  });
}

export function useApproveReview() {
  const invalidate = useWorkflowInvalidation();

  return useMutation({
    mutationFn: ({
      uuid,
      payload,
    }: {
      uuid: string;
      payload?: ApproveReviewPayload;
    }) => approveReview(uuid, payload),
    onSuccess: invalidate,
  });
}

export function useRejectReview() {
  const invalidate = useWorkflowInvalidation();

  return useMutation({
    mutationFn: ({
      uuid,
      payload,
    }: {
      uuid: string;
      payload: RejectReviewPayload;
    }) => rejectReview(uuid, payload),
    onSuccess: invalidate,
  });
}

export function useRequestChange() {
  const invalidate = useWorkflowInvalidation();

  return useMutation({
    mutationFn: ({
      uuid,
      payload,
    }: {
      uuid: string;
      payload: RequestChangePayload;
    }) => requestChange(uuid, payload),
    onSuccess: invalidate,
  });
}

export function useCreateChangeVersion() {
  const invalidate = useWorkflowInvalidation();

  return useMutation({
    mutationFn: ({
      uuid,
      payload,
    }: {
      uuid: string;
      payload: CreateChangeVersionPayload;
    }) => createChangeVersion(uuid, payload),
    onSuccess: invalidate,
  });
}

export function useCancelChangeRequest() {
  const invalidate = useWorkflowInvalidation();

  return useMutation({
    mutationFn: (uuid: string) => cancelChangeRequest(uuid),
    onSuccess: invalidate,
  });
}

export function useRejectChangeRequest() {
  const invalidate = useWorkflowInvalidation();

  return useMutation({
    mutationFn: ({
      uuid,
      payload,
    }: {
      uuid: string;
      payload: RejectChangeRequestPayload;
    }) => rejectChangeRequest(uuid, payload),
    onSuccess: invalidate,
  });
}

export function useApproveChangeRequest() {
  const invalidate = useWorkflowInvalidation();

  return useMutation({
    mutationFn: (uuid: string) => approveChangeRequest(uuid),
    onSuccess: invalidate,
  });
}

export function useDeprecateTranslation() {
  const invalidate = useWorkflowInvalidation();

  return useMutation({
    mutationFn: ({
      uuid,
      payload,
    }: {
      uuid: string;
      payload: DeprecateTranslationPayload;
    }) => deprecateTranslation(uuid, payload),
    onSuccess: invalidate,
  });
}

export function useRollbackTranslation() {
  const invalidate = useWorkflowInvalidation();

  return useMutation({
    mutationFn: ({
      uuid,
      targetVersion,
    }: {
      uuid: string;
      targetVersion: number;
    }) => rollbackTranslation(uuid, targetVersion),
    onSuccess: invalidate,
  });
}

export function useResubmitCorrection() {
  const invalidate = useWorkflowInvalidation();

  return useMutation({
    mutationFn: ({
      uuid,
      payload,
    }: {
      uuid: string;
      payload: ResubmitCorrectionPayload;
    }) => resubmitCorrection(uuid, payload),
    onSuccess: invalidate,
  });
}

export function useBulkSubmit() {
  const invalidate = useWorkflowInvalidation();

  return useMutation({
    mutationFn: (payload: BulkSubmitPayload) => bulkSubmit(payload),
    onSuccess: invalidate,
  });
}

export function useBulkApprove() {
  const invalidate = useWorkflowInvalidation();

  return useMutation({
    mutationFn: (payload: BulkApprovePayload) => bulkApprove(payload),
    onSuccess: invalidate,
  });
}

export function useBulkReject() {
  const invalidate = useWorkflowInvalidation();

  return useMutation({
    mutationFn: (payload: BulkRejectPayload) => bulkReject(payload),
    onSuccess: invalidate,
  });
}

export function useBulkDeprecate() {
  const invalidate = useWorkflowInvalidation();

  return useMutation({
    mutationFn: (payload: BulkDeprecatePayload) => bulkDeprecate(payload),
    onSuccess: invalidate,
  });
}

export function useBulkAssignTags() {
  const invalidate = useWorkflowInvalidation();

  return useMutation({
    mutationFn: (payload: BulkAssignTagsPayload) => bulkAssignTags(payload),
    onSuccess: invalidate,
  });
}
